import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@material-ui/core';
import { ArrowDropDownRounded, ArrowDropUpRounded } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import Pagination from './Pagination';

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const StyledTableContainer = styled(TableContainer)`
  height: 380px;
  overflow-x: auto;
`;

const StyledTableCell = styled(TableCell)`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 16px;
  text-overflow: ellipsis;
  min-width: 100px;
  max-width: 200px;
`;

const StyledHeaderCell = styled(TableCell)`
  font-weight: bold;
  padding: 0px 16px;
  text-wrap-mode: nowrap;
  text-align: ${props => (props.align === 'left' ? 'left' : 'center')};
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  height: 300px;
  width: 100%;
`;

const SortIconWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  margin-left: 6px;

  & .MuiSvgIcon-root {
    opacity: 0.5;
    font-size: 18px;
    margin: -6px;
`;

const useStyles = makeStyles({
    sortLabel: {
        '&:hover': {
            opacity: 1,
        },
        '& .MuiTableSortLabel-icon': {
            opacity: 1,
            visibility: 'visible !important',
        },
    },
    loadingSpinner: {
        color: '#1976d2',
    }
});

const SortIcon = React.memo(() => (
    <SortIconWrapper>
        <ArrowDropUpRounded />
        <ArrowDropDownRounded />
    </SortIconWrapper>
));

const CommonTable = ({
    columns,
    data,
    isLoading,
    orderBy,
    orderDirection,
    isSearchActive,
    searchField,
    onSort,
    actions,
    emptyMessage,
    pagination: {
        itemCount,
        currentPage,
        onNextPageClicked,
        onPrevPageClicked,
        onFirstPageClicked,
        onLastPageClicked,
        rowsPerPage,
        onChangeRowsPerPage,
        rowsPerPageOptions,
        hidePagination = false
    }
}) => {
    const classes = useStyles();

    const handleSort = (columnField) => {
        // Only handle sort if no search is active or if this is the search field
        if (isSearchActive && columnField !== searchField) return;
        
        const isAsc = orderBy === columnField && orderDirection === 'asc';
        onSort(columnField, isAsc ? 'desc' : 'asc');
    };

    const shouldShowSort = (column) => {
        // Show sort if:
        // 1. Column is sortable (sorting !== false) AND
        // 2. Either no search is active OR this is the search field
        return column.sorting !== false && (!isSearchActive || column.field === searchField);
    };

    const renderCell = (column, rowData) => {
        if (column.render) {
            return column.render(rowData);
        }
        return rowData[column.field];
    };

    const renderActions = (rowData) => {
        return actions.map((action, index) => {
            if (action(rowData).hidden) return null;

            return action(rowData).tooltip ? (
                <Tooltip key={index} title={action(rowData).tooltip}>
                    <IconButton
                        size="small"
                        onClick={(event) => action(rowData).onClick(event, rowData)}
                    >
                        {action(rowData).icon()}
                    </IconButton>
                </Tooltip>
            ) : (
                <IconButton
                    key={index}
                    size="small"
                    onClick={(event) => action(rowData).onClick(event, rowData)}
                >
                    {action(rowData).icon()}
                </IconButton>
            );
        });
    };

    const renderEmptyState = () => (
        <TableRow>
            <TableCell colSpan={columns.length + (actions ? 1 : 0)}>
                <EmptyStateContainer>
                    {emptyMessage}
                </EmptyStateContainer>
            </TableCell>
        </TableRow>
    );

    return (
        <TableWrapper>
            {isLoading && (
                <LoadingOverlay>
                    <CircularProgress
                        size={40}
                        className={classes.loadingSpinner}
                    />
                </LoadingOverlay>
            )}
            <StyledTableContainer component={Paper} elevation={0} square>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column, index) => (
                                <StyledHeaderCell
                                    key={index}
                                    align={column.cellStyle?.textAlign || 'left'}
                                >
                                    {shouldShowSort(column) ? (
                                        <TableSortLabel
                                            className={classes.sortLabel}
                                            active={orderBy === column.field}
                                            direction={orderBy === column.field ? orderDirection : 'asc'}
                                            onClick={() => handleSort(column.field)}
                                            IconComponent={orderBy === column.field ? ArrowDropDownRounded : SortIcon}
                                        >
                                            {column.title}
                                        </TableSortLabel>
                                    ) : (
                                        column.title
                                    )}
                                </StyledHeaderCell>
                            ))}
                            {actions && <StyledHeaderCell align="center">Actions</StyledHeaderCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length === 0 ? renderEmptyState() : (
                            data.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {columns.map((column, colIndex) => (
                                        <StyledTableCell
                                            key={colIndex}
                                            align={column.cellStyle?.textAlign || 'left'}
                                            style={column.cellStyle}
                                        >
                                            {renderCell(column, row)}
                                        </StyledTableCell>
                                    ))}
                                    {actions && (
                                        <StyledTableCell align="center">
                                            {renderActions(row)}
                                        </StyledTableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            {!hidePagination && (
                <Pagination
                    itemCount={itemCount}
                    currentPage={currentPage}
                    handleNextPage={onNextPageClicked}
                    handlePrevPage={onPrevPageClicked}
                    handleFirstPage={onFirstPageClicked}
                    handleLastPage={onLastPageClicked}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={onChangeRowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={(e, page) => {
                        console.log('Page changed:', page);
                    }}
                />
            )}
        </TableWrapper>
    );
};

export default CommonTable;